import { CartErrorTypeEnum } from '@sola/schema/dotcom/cart/errors.js';
import {
  ChildAvailabilityItem,
  ChildAvailabilitySearchResults,
  ChildHoldItem,
  ChildHoldResults,
  ChildQueryRequestItem,
} from '@sola/schema/dotcom/children/child.js';
import {
  BeneficiaryAvailabilityCheckState,
  BeneficiaryAvailabilityState,
  Hold,
  HoldCreateUpdateRequest,
  HoldRequestBase,
  HoldTypeEnum,
  HoldUpdateRequest,
  RemoveHoldResponse,
} from '@sola/schema/dotcom/children/hold.js';
import { UserError } from '@sola/shared-utils/errors-user.js';
import {
  userErrorAvailabilityGeneric,
  userErrorAvailabilityHold,
} from '@sola/shared-utils/errors/am.js';
import {
  userErrorCartHoldFailed,
  userErrorCartMultiHoldFailed,
} from '@sola/shared-utils/errors/cart.js';
import { isUserError } from '@sola/shared-utils/httpClient.js';

import { type SearchChildQueryParameters } from './types/requests';
import {
  childAvailabilitySearch,
  convertChildHold,
  createUpdateChildHold,
  getChildAvailabilityCheck,
  getChildAvailabilityDetails,
  getChildDetails,
  getChildRichMix,
  postChildAvailabilityQuery,
  removeChildHold,
} from './utils/routes';
import { getChildAvailabilityStateFromStatus } from './utils/utils';

const getHoldsPostBody = (
  body: HoldCreateUpdateRequest,
  holdType: HoldTypeEnum,
): HoldCreateUpdateRequest => {
  const errorMessage = 'Missing required body data to create a child hold';
  const { endDate, globalId, sourceCode } = body;

  if (!endDate || !globalId) {
    throw new Error(errorMessage);
  }
  switch (holdType) {
    case HoldTypeEnum.Convert: {
      if (!sourceCode) {
        throw new Error(errorMessage);
      }
      break;
    }
    case HoldTypeEnum.Create: {
      if (!globalId) {
        throw new Error(errorMessage);
      }
      break;
    }
    default:
      break;
  }

  return {
    ...body,
    holdingGlobalPartnerId: 'US',
    owner: body?.owner || 'dotcom',
    state: BeneficiaryAvailabilityState.HoldEcommerce,
  };
};

/**
 * Create hold object from POST request response data
 */
interface HoldsObject {
  holds: Hold[];
  failedHolds?: ChildHoldItem[];
}
const createHoldObj = ({
  children,
  endDate,
  holdType,
  status,
}: {
  children: ChildHoldItem[];
  status: ChildHoldResults['status'] | undefined;
  endDate?: HoldRequestBase['endDate'];
  holdType?: string;
}): HoldsObject => {
  let failedHolds;
  if (status !== 200) {
    const { fHolds: holdFailures, fHoldIds: holdIdFailures } = children.reduce(
      (
        { fHolds, fHoldIds }: { fHolds: ChildHoldItem[]; fHoldIds: string[] },
        cur,
      ) => {
        if (cur.code !== 2000) {
          // eslint-disable-next-line no-console
          console.debug('createHoldObj failure:', JSON.stringify(cur));
          return {
            fHolds: [...fHolds, cur],
            // used for error log
            fHoldIds: [...fHoldIds, cur.globalId],
          };
        }

        return {
          fHolds,
          fHoldIds,
        };
      },
      { fHolds: [], fHoldIds: [] },
    );

    failedHolds = holdFailures;
    // eslint-disable-next-line no-console
    console.debug(
      `createHoldObj: Status ${status}: Failed to ${holdType} global IDs ${String(holdIdFailures)}`,
    );
  }

  const holds = children.map((child) => {
    // if we've gotten this far we can use the ! operator here to assert
    // that the `holdId` value is NOT `null` or `undefined`
    return {
      id: child.holdId!,
      holdExpiration: endDate,
      childGlobalId: child.globalId,
    };
  });
  return {
    holds,
    ...(failedHolds &&
      failedHolds.length && {
        failedHolds,
      }),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const childResHandler = <T extends { [key: string]: any }>(resData?: T): T => {
  if (!resData) {
    throw new UserError({
      type: CartErrorTypeEnum.ChildNotFound,
    });
  }
  if (resData?.statusCode && resData?.statusCode !== 200) {
    throw userErrorAvailabilityGeneric({
      statusCode: resData.statusCode,
    });
  }

  return resData;
};

export const getChild = async (childId: string) => {
  if (!childId) {
    return;
  }

  return await getChildDetails(childId);
};

export const getChildAvailabilityById = async ({
  globalId,
}: {
  globalId: string;
}): Promise<ChildAvailabilityItem | undefined> => {
  if (!globalId) {
    // TODO :: __ErrorFramework__ do we need to throw here?
    return;
  }

  const childRes = await getChildAvailabilityDetails(globalId);

  return childResHandler<ChildAvailabilityItem>(childRes);
};

/**
 * Make a call to our proxy for the AM availability check and return the state enum
 * based on the status code response if
 */
export const childAvailabilityCheck = async (
  childId: string,
  sourceCode?: string,
): Promise<BeneficiaryAvailabilityCheckState | undefined> => {
  if (!childId) {
    throw new Error('Please provide a child id.');
  }

  const { response, statusCode } = await getChildAvailabilityCheck(
    childId,
    sourceCode,
  );

  if (statusCode === 200) {
    return BeneficiaryAvailabilityCheckState.Available;
  }

  return getChildAvailabilityStateFromStatus(statusCode, response?.status);
};

export const childAvailabilityQuery = async (
  body: ChildQueryRequestItem,
): Promise<ChildAvailabilitySearchResults | undefined> => {
  if (!body) {
    throw new Error('No post body provided.');
  }

  return await postChildAvailabilityQuery(body);
};

export const postHold = async (
  body: HoldCreateUpdateRequest,
): Promise<Hold | undefined> => {
  const { endDate, globalId, ...rest } = getHoldsPostBody(
    body,
    HoldTypeEnum.Create,
  );

  try {
    const { children, status } =
      (await createUpdateChildHold([{ endDate, globalId, ...rest }])) || {};

    /**
     * NOTE: With this current implementation, if a hold cannot be created when
     * adding an item to a cart, a blank cart is still returned with no items.
     * Is this the desired/intended behavior?
     * TODO: If the cart is still empty, yes. If there are other items in the cart, they should see those.
     *
     */
    if (status !== 200) {
      // TODO: We don't really need this differentiation in the hold create, but it would be good in the refresh/update logic and bringing in communication errors
      // const child = children?.[0];
      // if (child) {
      //   /**
      //    * A 4000 code means one of a few things
      //    * 1. Beneficiary Hold expired
      //    * 2. Holding Global Partner and the Global partner are different
      //    * 3. Holding Global Partner and submitting Global partner are different
      //    * 4. Beneficiary will be moved to Ineligible
      //    */
      //   if (child.code === 4000) {
      //     throw userErrorCartHoldLost({
      //       failedHoldIds: [globalId],
      //     });
      //   }
      //   /**
      //    * A 5000 code means one of a few things
      //    * 1. Beneficiary is Sponsored
      //    * 2. Beneficiary is in E-Commerce Hold State
      //    * 3. Beneficiary Hold Order is expired/Not linked to Beneficiary Profile
      //    * 4. Beneficiary is in No Money Hold State
      //    */
      //   if (child.code === 5000) {
      //     throw userErrorCartHoldSponsored({
      //       id: globalId,
      //     });
      //   }
      // }

      // TODO: It doesn't feel like proxy-api should be throwing userErrors - these are generally being sent back to the cart-api and it's cart-api that forms the error for the user display...
      throw userErrorAvailabilityHold({
        globalId,
        ...(status && { statusCode: status }),
      });
    }
    if (!children) {
      throw userErrorCartHoldFailed({ globalId });
    }

    const { holds } = createHoldObj({
      children,
      endDate,
      status,
      holdType: 'create',
    });
    return holds[0];
  } catch (error: unknown) {
    if (isUserError(error)) {
      throw error;
    }
    throw userErrorCartHoldFailed({ globalId, sourceCode: body.sourceCode });
  }
};

/**
 * Convert a hold for a child to `E-Commerce Hold`
 */
export const convertHold = async (body: HoldRequestBase): Promise<Hold> => {
  const { endDate, globalId, ...rest } = getHoldsPostBody(
    body,
    HoldTypeEnum.Convert,
  );

  const { children, status } =
    (await convertChildHold({ endDate, globalId, ...rest })) || {};

  if (status !== 200) {
    const statusCode = status;
    throw userErrorAvailabilityHold({
      globalId,
      ...(statusCode && { statusCode }),
    });
  }
  if (!children) {
    throw new UserError({
      type: CartErrorTypeEnum.HoldConvertFailed,
    });
  }

  const { holds } = createHoldObj({
    children,
    endDate,
    status,
    holdType: 'convert',
  });
  return holds[0];
};

export const removeHold = async (
  globalId: string,
  holdId?: string,
): Promise<RemoveHoldResponse | undefined> => {
  if (!globalId || !holdId) {
    // TODO :: add internal logging
    return;
  }

  return await removeChildHold(globalId, holdId);
};

export const createUpdateHolds = async (
  body: HoldUpdateRequest | HoldUpdateRequest[],
  endDate?: string,
): Promise<HoldsObject> => {
  const isBodyArray = Array.isArray(body);
  const isMultiHold = isBodyArray && body.length > 1;
  const holdsArray = isBodyArray ? body : [body];

  const holdsReqPayload = holdsArray.map((hold) => {
    return getHoldsPostBody(hold, HoldTypeEnum.Update);
  });

  try {
    const { children, status } =
      (await createUpdateChildHold(holdsReqPayload)) || {};

    // TODO :: @driscol if a `globalId` from the "holdsArray" is missing from the "children" response, the hold for those IDs failed
    if (!children || !status) {
      if (isMultiHold) {
        throw userErrorCartMultiHoldFailed();
      }

      throw userErrorCartHoldFailed({
        globalId: isBodyArray ? body[0].globalId : body.globalId,
        sourceCode: isBodyArray ? body[0].sourceCode : body.sourceCode,
      });
    }

    return createHoldObj({
      children,
      endDate,
      status,
      holdType: 'update',
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    // TODO :: __ErrorFramework__ figure this out
    if (isUserError(e)) {
      throw e;
    } else if (e.response?.status === 500) {
      throw userErrorAvailabilityHold({ statusCode: 500 });
    } else if (isMultiHold) {
      throw userErrorCartMultiHoldFailed();
    }

    throw userErrorCartHoldFailed({
      globalId: isBodyArray ? body[0].globalId : body.globalId,
    });
  }
};

/**
 * Get specified number of children via Rich Mix
 */
export const getRandomChildren = async (
  numberOfBeneficiaries = 1,
): Promise<ChildAvailabilitySearchResults | undefined> => {
  const childRes = await getChildRichMix({ numberOfBeneficiaries });

  return childResHandler<ChildAvailabilitySearchResults>(childRes);
};

export const childSearch = async (
  queryParams?: SearchChildQueryParameters,
): Promise<ChildAvailabilitySearchResults | undefined> => {
  const childRes = await childAvailabilitySearch(queryParams);

  return childResHandler<ChildAvailabilitySearchResults>(childRes);
};
